import { Injectable } from '@angular/core';
import {
  TixGetFeesForVenueGQL,
  TixBuyerProductFragmentFragment
} from '@tix/data-access';
import { OrderTicket } from '@tix/event-buyer/services';
import { dollarFigureToNumber, formatAsDollarFigure } from '@tix/shared/utils';
import {
  Calculator,
  Discounts,
  onlineProcessingFeePreset,
  Order,
  Ticket,
  Addon
} from 'asylumtix-calculator';

interface EnhancedOrder extends Omit<Order, 'tickets' | 'addons'> {
  tickets: (Ticket & { ticketConfigId: string })[];
  addons: (Addon & { productConfigId: string })[];
}

export interface OrderCalculationResult {
  ticketsSubtotal: number;
  addonsSubtotal: number;
  addonsTotal: number;
  totalProcessingFee: number;
  totalPlatformFee: number;
  totalVenueFee: number;
  feesTotal: number;
  discountAmount: number;
  amountToPay: number;
  totalWithNoFees: number;
  hasItems: boolean;
  totalItems: number;
  ticketTypeTotals: Record<string, number>;
}

@Injectable({
  providedIn: 'root'
})
export class OrderCalculationsService {
  private calculatorService?: Calculator;
  private venuePlatformFee = 3;

  constructor(private getFeesForVenueQuery: TixGetFeesForVenueGQL) {}

  async initializeCalculator(venueId: string): Promise<void> {
    if (!venueId) return;

    const res = await this.getFeesForVenueQuery.fetch({ venueId }).toPromise();

    this.venuePlatformFee =
      res.data.fees.filter(f => f.feeType === 'platform')[0]?.amount || 3;

    this.calculatorService = new Calculator({
      platformFee: { amount: this.venuePlatformFee },
      processingFee: onlineProcessingFeePreset,
      processingFeePrecision: 2
    });
  }

  async calculateOrderTotals(
    tickets: OrderTicket[],
    products: (TixBuyerProductFragmentFragment & { selected: number })[] = [],
    discount?: any,
    donationAmount = 0
  ): Promise<OrderCalculationResult> {
    if (!this.calculatorService) {
      throw new Error('Calculator service not initialized');
    }

    const order: EnhancedOrder = this.buildOrder(
      tickets,
      products,
      discount,
      donationAmount
    );

    const ticketsSubtotal = this.calculatorService.getTicketsSubtotal(
      order.tickets
    );
    const addonsSubtotal = this.calculatorService.getAddonsSubtotal(
      order.addons
    );
    const addonsTotal =
      addonsSubtotal + this.calculatorService.getAddonsTax(order.addons);
    const discountAmount = this.calculatorService.getTotalDiscountAmount(order);
    const totalWithNoFees = ticketsSubtotal + addonsSubtotal;

    const totalProcessingFee =
      this.calculatorService.getProcessingFeeForOrder(order);
    const totalPlatformFee = this.calculatorService.getPlatformFee(order);
    const totalVenueFee = this.calculatorService.getVenueFee(order);

    const includeFees = tickets.some(ticket => ticket.includeFees);
    let feesTotal: number;

    if (!includeFees) {
      feesTotal = totalProcessingFee + totalPlatformFee + totalVenueFee;
    } else {
      const addonsProcessingFee = order.addons.reduce(
        (total, addon) =>
          total + this.calculatorService!.getAddonProcessingFee(order, addon),
        0
      );
      const donationProcessingFee =
        this.calculatorService.getProcessingFeeForDonation(order);
      feesTotal = addonsProcessingFee + donationProcessingFee;
    }

    const amountToPay = this.calculatorService.getTotalToPay(order) || 0;

    const hasItems =
      tickets.some(t => t.ticketQty > 0) || products.some(p => p.selected > 0);

    const totalItems =
      tickets.reduce((sum, t) => sum + (t.ticketQty || 0), 0) +
      products.reduce((sum, p) => sum + (p.selected || 0), 0);

    // Calculate totals per ticket type using getAmountPaidForTicket
    const ticketTypeTotals: Record<string, number> = {};
    tickets.forEach(ticket => {
      if (ticket.ticketQty > 0) {
        const ticketsForType = order.tickets.filter(
          t => t.ticketConfigId === ticket.ticketConfigId
        );
        const totalForType = ticketsForType.reduce(
          (sum, t) =>
            sum + this.calculatorService!.getAmountPaidForTicket(order, t),
          0
        );
        ticketTypeTotals[ticket.ticketConfigId] = totalForType;
      }
    });

    return {
      ticketsSubtotal,
      addonsSubtotal,
      addonsTotal,
      totalProcessingFee,
      totalPlatformFee,
      totalVenueFee,
      feesTotal,
      discountAmount,
      amountToPay,
      totalWithNoFees,
      hasItems,
      totalItems,
      ticketTypeTotals
    };
  }

  private buildOrder(
    tickets: OrderTicket[],
    products: (TixBuyerProductFragmentFragment & { selected: number })[],
    discount?: any,
    donationAmount = 0
  ): EnhancedOrder {
    const orderTickets = this.buildOrderTickets(tickets);
    const orderAddons = this.buildOrderAddons(products);
    const appliedDiscount = this.buildDiscount(discount);

    return {
      tickets: orderTickets,
      addons: orderAddons,
      discount: appliedDiscount,
      donation: donationAmount ? { amount: donationAmount } : undefined
    };
  }

  private buildOrderTickets(
    tickets: OrderTicket[]
  ): (Ticket & { ticketConfigId: string })[] {
    const ticketArrays = tickets
      .filter(ticket => ticket.ticketQty > 0)
      .map(ticket =>
        Array.from({ length: ticket.ticketQty }, () => ({
          id: `${ticket.ticketConfigId}-${Math.random()}`,
          venueFee: dollarFigureToNumber(ticket.venueFee),
          price: dollarFigureToNumber(ticket.ticketPrice),
          includeFeesInPrice: ticket.includeFees || false,
          ticketConfigId: ticket.ticketConfigId
        }))
      );

    return ticketArrays.reduce((acc, curr) => acc.concat(curr), []);
  }

  private buildOrderAddons(
    products: (TixBuyerProductFragmentFragment & { selected: number })[]
  ): (Addon & { productConfigId: string })[] {
    const addonArrays = products
      .filter(p => p.selected > 0)
      .map(product =>
        Array.from({ length: product.selected }, () => ({
          id: `${product.eventProductId}-${Math.random()}`,
          price: dollarFigureToNumber(product.price),
          tax: product.tax,
          productConfigId: product.eventProductId
        }))
      );

    return addonArrays.reduce((acc, curr) => acc.concat(curr), []);
  }

  private buildDiscount(discount?: any) {
    if (!discount) return undefined;

    switch (discount.type) {
      case '$ Discount':
        return new Discounts.StaticDiscount({
          amount: discount.getAmount!,
          appliedTo: discount?.appliedTo === 'Ticket' ? 'ticket' : 'reservation'
        });
      case '% Discount':
        return new Discounts.PercentageDiscount({
          amount: discount.getAmount! / 100
        });
      case 'Buy X Get Y Free':
        return new Discounts.BuyXGetYFreeDiscount({
          freeAmount: discount.getAmount!,
          buyAmount: discount?.buyAmount!
        });
      case 'Spend $X, Get $Y Off':
        return new Discounts.SpendXGetYOffDiscount({
          offAmount: discount.getAmount!,
          spendAmount: discount?.buyAmount!
        });
      default:
        return undefined;
    }
  }

  formatAsDollarFigure = formatAsDollarFigure;
}
